import { createContext, useContext, type ReactNode } from 'react'
import type { FeatureWebModule, RosterRowTabContribution, WebModule } from './webModule.js'
import type { HelpTopic } from './help/types.js'

/**
 * The registry as the running app sees it: every module and every add-on,
 * handed down from `main.tsx` so a screen deep inside a module (a roster
 * row's tabs, the admin's worker detail) reads the list through this hook
 * rather than importing a file that imports every module - which would be
 * a cycle evaluated at load time, the one kind the rules forbid.
 */
export interface WebModulesRegistry {
  /** Every module, in the order the shells list their contributions. Typed with no admin api: a module renders no admin page itself. */
  modules: readonly WebModule<never>[]
  /** Every add-on, in catalog order. */
  features: readonly FeatureWebModule[]
}

const EMPTY: WebModulesRegistry = { modules: [], features: [] }

const WebModulesContext = createContext<WebModulesRegistry>(EMPTY)

export function WebModulesProvider({ registry, children }: { registry: WebModulesRegistry; children: ReactNode }) {
  return <WebModulesContext.Provider value={registry}>{children}</WebModulesContext.Provider>
}

/** Every add-on, for a shell or screen that lists their contributions. */
export function useFeatures(): readonly FeatureWebModule[] {
  return useContext(WebModulesContext).features
}

/** Every module's and add-on's help topics, for a help icon to find its own by id. */
export function useHelpTopics(): readonly HelpTopic[] {
  const { modules, features } = useContext(WebModulesContext)
  return [...modules.flatMap((module) => module.helpTopics ?? []), ...features.flatMap((feature) => feature.helpTopics ?? [])]
}

/** The tabs modules add to an open roster row, in module order. */
export function useRosterRowTabs(): readonly RosterRowTabContribution[] {
  return useContext(WebModulesContext).modules.flatMap((module) => module.rosterRowTabs ?? [])
}
